import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { omit } from 'lodash-es';

import { continueApplicationProcessing } from './application';
import { Address, INITIAL_ADDRESS } from '../../../domain/address';
import { PersonalInfo } from '../../../domain/person';
import { RootState } from '../../../store';

const INITIAL_PERSONAL_INFO: PersonalInfo = {
  firstName: '',
  lastName: '',
  dob: '',
  ssnLast4: '',
  address: INITIAL_ADDRESS,
  authorizedRep: false,
  beneficialOwner: false,
  controlPerson: false,
};

type PersonalInfoState = {
  info: PersonalInfo;
};

const initialState: PersonalInfoState = {
  info: INITIAL_PERSONAL_INFO,
};

const slice = createSlice({
  name: 'personalInfo',
  initialState,
  reducers: {
    savePersonalInfo: (state, action: PayloadAction<PersonalInfo>) => ({
      ...state,
      info: action.payload,
    }),
    savePersonalInfoLargeBusiness: (state, action: PayloadAction<PersonalInfo>) => ({
      ...state,
      info: action.payload,
    }),
    updatePersonalAddress: (state, action: PayloadAction<Address>) => ({
      ...state,
      info: {
        ...state.info,
        address: action.payload,
      },
    }),
    updatePersonalInfoOwnershipAndControl: (
      state,
      action: PayloadAction<Pick<PersonalInfo, 'beneficialOwner' | 'controlPerson'>>,
    ) => ({
      ...state,
      info: {
        ...state.info,
        ...action.payload,
      },
    }),
    resetPersonalInfoAttestations: (state) => ({
      ...state,
      info: {
        ...state.info,
        authorizedRep: false,
        beneficialOwner: false,
      },
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(
      continueApplicationProcessing,
      (
        state,
        {
          payload: {
            response: { application },
          },
        },
      ) => {
        const applicant = (application?.people ?? []).find(({ isApplicant }) => isApplicant);
        const person = applicant ? omit(applicant, 'isApplicant') : applicant;

        return {
          ...state,
          info: {
            ...INITIAL_PERSONAL_INFO,
            ...person,
            address: {
              ...INITIAL_ADDRESS,
              ...person?.address,
            },
          },
        };
      },
    );
  },
});

export const personalInfoReducer = slice.reducer;
export const {
  savePersonalInfo,
  savePersonalInfoLargeBusiness,
  updatePersonalAddress,
  resetPersonalInfoAttestations,
  updatePersonalInfoOwnershipAndControl,
} = slice.actions;

export const selectPersonalInfo = (state: RootState) => state.personalInfo.info;
